import type { Order, WaybillBayanStatus, WaybillBlocker } from '@hero/schema';

/**
 * The one thing a government-waybill entry offers, given the connector's state.
 *
 * `canceled` and `order_canceled` are separate because they need different
 * copy: one says the document was voided, the other says the load was.
 */
export type GovernmentWaybillState =
  | 'connect'
  | 'needs_assignment'
  | 'ready'
  | 'blocked'
  | 'issuing'
  | 'failed'
  | 'download'
  | 'canceled'
  | 'order_canceled';

export interface ResolveGovernmentWaybillStateArgs {
  order: Order;
  /**
   * Live status read, when the surface has one.
   *
   * Structural rather than the full `OrderWaybillBayanStatus` so a caller with
   * only the order document (a list row, which must not fetch a status per
   * item) can pass nothing and still get a usable answer.
   */
  status?: { status?: WaybillBayanStatus; blockers?: WaybillBlocker[] };
  /**
   * True once `/apps/logisti/waybill/status` has settled for this order.
   *
   * When set, trust the live read — do not fall back to a denormalised
   * `order.waybillBayan.status` that may still say `pending` after the backend
   * cleared a phantom claim.
   */
  statusFetched?: boolean;
  /**
   * Whether the viewing Org can issue at all — on its own Bayan licence or on
   * Hero's.
   *
   * Deliberately not "is the connector installed". An Org covered by platform
   * filing has no install and issues perfectly well, so branching on the
   * install list is what would put a Connect button in front of someone whose
   * documents are already going through.
   */
  canIssue: boolean;
}

/**
 * Which single thing the Logisti entry offers right now.
 *
 * The connector has several states a user can be in and only one of them is
 * "press to issue". Splitting those across several menu entries meant every
 * order showed two or three Logisti rows, most of them greyed out — so the
 * menu explained the connector's internals instead of offering an action. One
 * entry, one state, one next step.
 *
 * Shared between the web actions menu and carrier-app's actions sheet. The
 * ordering below is load-bearing and easy to get subtly wrong twice: keeping
 * it in one place is what stops the two platforms from disagreeing about, say,
 * whether a canceled order with blockers reads as `blocked` or `order_canceled`.
 */
export const resolveGovernmentWaybillState = ({
  order,
  status,
  statusFetched = false,
  canIssue,
}: ResolveGovernmentWaybillStateArgs): GovernmentWaybillState => {
  if (!canIssue) return 'connect';

  const blockers = status?.blockers ?? order.waybillBayan?.blockers ?? [];
  const value = statusFetched ? status?.status : (status?.status ?? order.waybillBayan?.status);

  if (value === 'issued' || value === 'closed') return 'download';
  if (order.status?.order === 'canceled') return 'order_canceled';
  if (value === 'canceled') return 'canceled';

  // Ahead of `blocked`: "assign a driver" is the one next step, and showing a
  // blocker count first would ask the user to go fix details on records that
  // aren't chosen yet. A وثيقة names a specific driver and a specific truck,
  // so there is nothing to issue until both are assigned.
  if (!order.driver?._id || !order.vehicle?._id) return 'needs_assignment';

  // Validation blockers are not an in-flight issuance — check before `pending`.
  if (blockers.length > 0) return 'blocked';

  if (value === 'pending') return 'issuing';
  if (value === 'failed') return 'failed';
  return 'ready';
};

/** States where the entry is inert — shown for explanation, not for pressing. */
export const isGovernmentWaybillStateInert = (state: GovernmentWaybillState): boolean =>
  state === 'issuing' ||
  state === 'canceled' ||
  state === 'order_canceled' ||
  state === 'needs_assignment';
